import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { useCreateProduct } from "../hooks/useCreateProduct";
import type { BillingInterval } from "../hooks/useProduct";

export function NewProduct() {
  useEffect(() => { document.title = "New Product | PayWithPull"; }, []);

  const navigate = useNavigate();
  const { mutate, isPending, error } = useCreateProduct();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("month");
  const [billingIntervalCount, setBillingIntervalCount] = useState("1");
  const [requiresDeliveryAddress, setRequiresDeliveryAddress] = useState(false);
  const [trialDays, setTrialDays] = useState("");
  const [trialPrice, setTrialPrice] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate(
      {
        name,
        description,
        price: parseFloat(price),
        currency,
        billing_interval: billingInterval,
        billing_interval_count: parseInt(billingIntervalCount, 10),
        active: true,
        requires_delivery_address: requiresDeliveryAddress,
        trial_days: trialDays ? parseInt(trialDays, 10) : null,
        trial_price: trialDays && trialPrice ? parseFloat(trialPrice) : null,
      },
      {
        onSuccess: () => navigate("/dashboard"),
      }
    );
  };

  return (
    <DashboardLayout>
      <h1 className="text-2xl font-bold text-gray-900 mb-8">New Product</h1>

      <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-2xl">
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              id="name"
              type="text"
              required
              maxLength={255}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              required
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                Price
              </label>
              <input
                id="price"
                type="number"
                required
                min="0.01"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              />
            </div>

            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              >
                <option value="GBP">GBP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="billing_interval_count" className="block text-sm font-medium text-gray-700 mb-1">
                Bill every
              </label>
              <input
                id="billing_interval_count"
                type="number"
                required
                min={1}
                step={1}
                value={billingIntervalCount}
                onChange={(e) => setBillingIntervalCount(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              />
            </div>

            <div>
              <label htmlFor="billing_interval" className="block text-sm font-medium text-gray-700 mb-1">
                Interval
              </label>
              <select
                id="billing_interval"
                value={billingInterval}
                onChange={(e) => setBillingInterval(e.target.value as BillingInterval)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              >
                <option value="day">Day(s)</option>
                <option value="week">Week(s)</option>
                <option value="month">Month(s)</option>
                <option value="year">Year(s)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="trial_days" className="block text-sm font-medium text-gray-700 mb-1">
                Trial length (days)
              </label>
              <input
                id="trial_days"
                type="number"
                min={1}
                max={365}
                step={1}
                placeholder="No trial"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                New subscribers start paying when the trial ends. One trial per customer.
              </p>
            </div>

            <div>
              <label htmlFor="trial_price" className="block text-sm font-medium text-gray-700 mb-1">
                Trial price
              </label>
              <input
                id="trial_price"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="Free"
                value={trialPrice}
                disabled={!trialDays}
                onChange={(e) => setTrialPrice(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none disabled:bg-gray-50 disabled:text-gray-400"
              />
              <p className="text-xs text-gray-500 mt-1">
                Charged once at checkout. Leave blank for a free trial.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input
              id="requires_delivery_address"
              type="checkbox"
              checked={requiresDeliveryAddress}
              onChange={(e) => setRequiresDeliveryAddress(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <label htmlFor="requires_delivery_address" className="block text-sm font-medium text-gray-700">
                Requires delivery address
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Customers will be asked for a delivery address at checkout. This cannot be changed after the product is created.
              </p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-500">{error.message}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {isPending ? "Creating..." : "Create Product"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
